import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { MapService } from '../../map/services/map.service';
import { LocateService } from '../../map/services/locate.service';
import { GeoService } from '../../map/services/geo.service';

import { LengthUnit } from '../../models/map.interface';

import cloneDeep from 'lodash/cloneDeep';

interface CCS {
	northing: string;
	easting: string;
}

// Universal Transverse Mercator
// Upper Left:
// Northing 4182000
// Zone 10
// Easting 551000
// North
//
// Bottom Right:
// Northing 3763000
// Zone 11
// Easting 385000
// North
interface UTM {
	utmHemisphere: "North" | "South";
	northing: string;
	easting: string;
	zone: string;
}

// Decimal Degrees
// Upper Left (Y/X): 37.7749, -122.4194
// Bottom Right (Y/X): 34.0522, -118.2437
interface DDD {
	x: string;
	y: string;
}

// Degrees Minutes Seconds
// Upper Left (Y/X): 37° 46' 30" N, 122° 25' 10" W
// Bottom Right (Y/X): 34° 3' 8" N, 118° 14' 37" W
interface DMS {
	xh: "East" | "West";
	xd: string;
	xm: string;
	xs: string;
	yh: "North" | "South";
	yd: string;
	ym: string;
	ys: string;
}

// Military Grid Reference System
// Upper Left:   10SEG5102182000
// Bottom Right: 11SLT8500030000
interface MGRS {
	point: string;
}

interface ResetAOI {
	radius: string;
	radiusType: string;
	shape: string;
	mode: string;
	ccs: CCS;
	ccs2: CCS;
	//	ccsnad83: string,
	ddd1: DDD;
	ddd2: DDD;
	utm1: UTM;
	utm2: UTM;
	dms1: DMS;
	dms2: DMS;
	mgrs1: MGRS;
	mgrs2: MGRS;
}


/**
 * Component to execute a geographic search.
 * The Locate options processed by this controller are set in config.esri.locate:
 *  geometryList
 *  township/rancho
 *  dynamic
 *  measureToGeometry
 *  ces (default - locate by manual-entry coordinates) 
 * 
 * All but the default appear to be either custom (San Diego?) options or demoware. 
 */
@Component({
	selector: 'geo-locate',
	templateUrl: './geo-locate.component.html',
	styleUrl: './geo-locate.component.css'
})
export class GeoLocateComponent implements OnInit, OnDestroy {

	private userEventSubscription!: Subscription;

	LOCALIZATION: any;

	locateform: any = {};
	locateControls: any[] | null = null;
	locateValues: any = {};
	aoi: ResetAOI = this.createResetAOI();
	lengthUnit: LengthUnit[] = [];
	searchBuffer: { value: string; } = { value: "" };

	constructor(
		private configService: ConfigService,
		private userService: UserService,
		private mapService: MapService,
		private locateService: LocateService,
		private geoService: GeoService
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;

		this.locateControls = this.locateService.getLocateUnit();
		this.locateform.locateControl = this.locateControls == null ? null : this.locateControls[0];
		this.lengthUnit = this.mapService.getLengthUnits();
		this.searchBuffer.value = this.configService.getConfig().searchBuffer;

		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if ('localizationLoaded' === event) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			}
		});
	}

	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}
	}

	updateLocateControl(): void {
		this.customAoiClear();
	}

	customAoiSearch(config: any): void {
		this.locateValues.noResults = false;
		this.locateValues.more = false;
		this.locateValues.results = null;
		this.locateform.locateControl.APN = null;
		this.locateform.locateControl.milepost = null;
		this.locateform.locateControl.image = null;

		if (config.type === "dynamic") {
			this.locateService.findDynamic(config.url, this.locateform.locateControl.querySearchFields, this.locateform.locateControl.queryfields, config.resultFields).then(
				(results: any) => {
					if (!results) {
						this.locateValues.noResults = true;
						this.locateValues.results = null;
					}
					if (results && results.length == 0) {
						this.locateValues.noResults = true;
						this.locateValues.results = null;
					}
					else if (results.length > 5) {
						this.locateValues.more = true;
						this.locateValues.results = results.slice(0, 5);
						this.locateValues.noResults = false;
					} else {
						this.locateValues.results = results;
						this.locateValues.more = false;
						this.locateValues.noResults = false;
					}
				},
				(err: any) => {
					console.error("Error searching location", err);
					this.locateValues.noResults = true
				}
			);


		} else if (config.type === "image_path" || config.type === "image_point") {
			let start = null;
			let end = null;

			if (this.locateValues.image_date_range && this.locateValues.image_date_range.startDate && this.locateValues.image_date_range.endDate) {
				start = this.locateValues.image_date_range.startDate.format("MM/DD/YYYY");
				end = this.locateValues.image_date_range.endDate.format("MM/DD/YYYY");
			}

			this.locateService.findImage(config.url, this.locateValues.filename, start, end, this.locateValues.created_user).then(
				(results: any) => {
					if (!results) {
						this.locateValues.noResults = true;
						this.locateValues.results = null;
					}
					if (results && results.length == 0) {
						this.locateValues.noResults = true;
						this.locateValues.results = null;
					}
					else if (results.length > 5) {
						this.locateValues.more = true;
						this.locateValues.results = results.slice(0, 5);
						this.locateValues.noResults = false;
					} else {
						this.locateValues.results = results;
						this.locateValues.more = false;
						this.locateValues.noResults = false;
					}
					//				blockUI.stop('Searching ...');
				},
				(err: any) => {
					console.error("Error searching location", err);
					//				blockUI.stop('Searching ...');
					this.locateValues.noResults = true
				}
			);

		} else if (config.type === "APN") {
			// TODO: esriAutoCompleteService?
			//			this.esriAutoCompleteService.search(this.locateValues.value, config).then(function(results) {
			//				if (results == null || results.features.length == 0) {
			//					this.locateValues.noResults = true;
			//					this.locateValues.results = null;
			//				}
			//				else {
			//					this.locateValues.noResults = false;
			//
			//					if (results.features.length > 5) {
			//						this.locateValues.more = true;
			//					}
			//					this.locateValues.results = results.features.slice(0, 5);
			//				}
			//				//				blockUI.stop('Searching ...');
			//			}, function(error) {
			//				//				blockUI.stop('Searching ...');
			//				this.locateValues.noResults = true
			//			});
		}
	}

	async updateAoiManually(locate: boolean): Promise<void> {
		this.mapService.broadcastOpenMapEvent();

		let clonedAOI = cloneDeep(this.aoi);
		let answer = await this.geoService.processManualEntry(clonedAOI, locate);

		if (answer) {
			let newAOI = this.createResetAOI();
			newAOI.radiusType = this.aoi.radiusType;
			newAOI.radius = this.aoi.radius;
			newAOI.shape = this.aoi.shape;
			newAOI.mode = this.aoi.mode;

			newAOI.ddd1.x = answer.point1.x;
			newAOI.ddd1.y = answer.point1.y;
			if (answer.point2) {
				newAOI.ddd2.x = answer.point2.x;
				newAOI.ddd2.y = answer.point2.y;
			}

			newAOI.utm1.utmHemisphere = answer.point1.utmHemisphere;
			newAOI.utm1.northing = answer.point1.northing;
			newAOI.utm1.easting = answer.point1.easting;
			newAOI.utm1.zone = answer.point1.zone;
			if (answer.point2) {
				newAOI.utm2.utmHemisphere = answer.point2.utmHemisphere;
				newAOI.utm2.northing = answer.point2.northing;
				newAOI.utm2.easting = answer.point2.easting;
				newAOI.utm2.zone = answer.point2.zone;
			}

			newAOI.dms1.xh = answer.point1.xh;
			newAOI.dms1.xd = answer.point1.xd;
			newAOI.dms1.xm = answer.point1.xm;
			newAOI.dms1.xs = answer.point1.xs;
			newAOI.dms1.yh = answer.point1.yh;
			newAOI.dms1.yd = answer.point1.yd;
			newAOI.dms1.ym = answer.point1.ym;
			newAOI.dms1.ys = answer.point1.ys;
			if (answer.point2) {
				newAOI.dms2.xh = answer.point2.xh;
				newAOI.dms2.xd = answer.point2.xd;
				newAOI.dms2.xm = answer.point2.xm;
				newAOI.dms2.xs = answer.point2.xs;
				newAOI.dms2.yh = answer.point2.yh;
				newAOI.dms2.yd = answer.point2.yd;
				newAOI.dms2.ym = answer.point2.ym;
				newAOI.dms2.ys = answer.point2.ys;
			}

			newAOI.mgrs1.point = answer.point1.point;
			if (answer.point2) {
				newAOI.mgrs2.point = answer.point2.point;
			}

			this.aoi = newAOI;
		} else {
			let newScope = this.createResetAOI();

			if (this.aoi.mode === "DDD") {
				newScope.ddd1 = this.aoi.ddd1;
				if (this.aoi.shape === "rectangle") {
					newScope.ddd2 = this.aoi.ddd2;
				}
			} else if (this.aoi.mode === "DMS") {
				newScope.dms1 = this.aoi.dms1;
				if (this.aoi.shape === "rectangle") {
					newScope.dms2 = this.aoi.dms2;
				}
			} else if (this.aoi.mode === "UTM") {
				newScope.utm1 = this.aoi.utm1;
				if (this.aoi.shape === "rectangle") {
					newScope.utm2 = this.aoi.utm2;
				}
			} else if (this.aoi.mode === "CCS") {
				// newScope.ccsnad83 = this.aoi.ccsnad83;
				newScope.ccs = this.aoi.ccs;
				if (this.aoi.shape === "rectangle") {
					newScope.ccs2 = this.aoi.ccs2;
				}
			} else if (this.aoi.mode === "MGRS") {
				newScope.mgrs1 = this.aoi.mgrs1;
				if (this.aoi.shape === "rectangle") {
					newScope.mgrs2 = this.aoi.mgrs2;
				}
			}

			newScope.radius = this.aoi.radius;
			newScope.radiusType = this.aoi.radiusType;
			newScope.mode = this.aoi.mode;
			newScope.shape = this.aoi.shape;

			this.aoi = newScope;
		}
	}

	private customAoiClear(): void {
		this.locateValues.noResults = false;
		this.locateValues.more = false;
		this.locateValues.results = null;
		this.locateValues.value = "";
		this.locateValues.queryvalue = null;
		this.locateValues.queryfield = null;
		this.locateform.locateControl.APN = null;
		this.locateform.locateControl.milepost = null;
		this.locateform.locateControl.image = null;
	}

	private createResetAOI(): ResetAOI {
		return {
			radius: "",
			radiusType: "meters",
			shape: "rectangle",
			mode: "DDD",
			ccs: {
				northing: "",
				easting: ""
			},
			ccs2: {
				northing: "",
				easting: ""
			},
			//	ccsnad83: "2",
			ddd1: {
				x: "",
				y: ""
			},
			ddd2: {
				x: "",
				y: ""
			},
			utm1: {
				utmHemisphere: "North",
				northing: "",
				easting: "",
				zone: ""
			},
			utm2: {
				utmHemisphere: "North",
				northing: "",
				easting: "",
				zone: ""
			},
			dms1: {
				xh: "East",
				xd: "",
				xm: "",
				xs: "",
				yh: "North",
				yd: "",
				ym: "",
				ys: ""
			},
			dms2: {
				xh: "East",
				xd: "",
				xm: "",
				xs: "",
				yh: "North",
				yd: "",
				ym: "",
				ys: ""
			},
			mgrs1: {
				point: "",
			},
			mgrs2: {
				point: "",
			}
		};
	}

	// TODO: Event is issued from user selection in map.controller.js
	//	this.$on("mapControl.lengthUnit", function (evt, x) {
	//	x = (x || srsApp.CONFIG.lengthUnit);
	//	   this.lengthUnits = mapService.getLengthUnits();
	//	   this.lengthUnit = x;
	//	   let lengthObject = $.grep(this.lengthUnits, function (n, i) {
	//	       if (n.value == this.lengthUnit || n.engineUnit == this.lengthUnit) {
	//	           return n.name;
	//	       }
	//	   })[0];
	//	   this.lengthUnitValue = lengthObject.engineUnit;
	//	   this.searchBufferMultiplier = lengthObject.toMeter;
	//	});	

	// TODO: This call is issued from locate.html (custom San Diego component?)
	//	$scope.processLocate = function (search) {
	//	    if ($scope.locateform.locateControl) {
	//	        if (!search) {
	//	            $rootScope.$broadcast("openRecordInMap");
	//	        }
	//	        switch ($scope.locateform.locateControl.type) {
	//	            case "coordinate":
	//	                var result = geoService.locateCoordinate($scope.locateform.locateControl.coordinate);
	//	                //if result has error, blast it
	//	                if (result.error) {
	//	                    alertsService.error({
	//	                        message: result.error,
	//	                        startTop: $("#map").offset().top
	//	                    });
	//	                    return;
	//	                }
	//	                esriLoader.loadModules(["esri/geometry/Point"], srsApp.CONFIG.version.arcgisVersion).then(
	//					function ([Point]) {
	//	                        var point = new Point(result.x, result.y);
	//	                        $rootScope.$broadcast("AddPointLocate&Zoom", {
	//	                            point: point,
	//	                            zoom: 9,
	//	                            search: search
	//	                        });
	//	                    });
	//	                break;
	//	            case "geometryList":
	//	                var sel = $scope.locateValues.selectedGeometry;
	//	                $rootScope.$broadcast("AddLocation&Zoom", { geometry: sel.geometry, search: search });
	//	                break;
	//	            case "township/rancho":
	//	                var sel;
	//	                if ($scope.locateform.locateControl.radio === "rancho") {
	//	                    sel = $scope.locateform.locateControl.selectedRancho;
	//	                } else if ($scope.locateform.locateControl.radio === "township") {
	//	                    sel = $scope.locateform.locateControl.selectedSection;
	//	                };
	//	                $rootScope.$broadcast("AddLocation&Zoom", { geometry: sel.geometry, search: search });
	//	                break;
	//	            case "address":
	//	                var streetaddress = $scope.locateValues.streetaddress ? $scope.locateValues.streetaddress : "";
	//	                var streetcity = $scope.locateValues.streetcity ? $scope.locateValues.streetcity : "";
	//	                var streetregion = $scope.locateValues.streetregion ? $scope.locateValues.streetregion : "";
	//	                var zip = $scope.locateValues.zip ? $scope.locateValues.zip : "";
	//
	//	                if ($scope.isEmpty(streetaddress) && $scope.isEmpty(streetcity) && $scope.isEmpty(streetregion) && $scope.isEmpty(zip)) {
	//	                    alertsService.error({
	//	                        title: "Input Error",
	//	                        message: "At least one field for address searching is required.",
	//	                        startTop: $("#map").offset().top
	//	                    });
	//	                    return;
	//	                }
	//
	//	                locateService.findAddressByLocator($scope.locateform.locateControl.url, streetaddress, zip, true, function (result) {
	//	                    esriLoader.loadModules(["esri/geometry/Point"], srsApp.CONFIG.version.arcgisVersion).then(
	//						 function ([Point]) {
	//	                            if (!result || !result.candidates || result.candidates.length == 0 || result.candidates[0].score == 0) {
	//	                                alertsService.error({
	//	                                    title: "Address Not Found",
	//	                                    message: "Try a more specific address.",
	//	                                    startTop: $("#map").offset().top
	//	                                });
	//	                                return;
	//	                            }
	//	                            var coord = result.candidates[0].location;
	//	                            var point = new Point(coord.x, coord.y);
	//	                            $rootScope.$broadcast("AddPointLocate&Zoom", {
	//	                                point: point,
	//	                                zoom: 18,
	//	                                search: search
	//	                            });
	//	                        })
	//	                });
	//
	//	                break;
	//	            case "intersection":
	//	                var street1 = $scope.locateValues.street1;
	//	                var street2 = $scope.locateValues.street2;
	//	                var zip = $scope.locateValues.zip;
	//
	//	                if ($scope.isEmpty(street1) || $scope.isEmpty(street2) || ($scope.isEmpty(zip) && $scope.locateform.locateControl.zip)) {
	//	                    alertsService.error({
	//	                        message: $scope.LOCALIZATION.fillAllFields,
	//	                        startTop: $("#map").offset().top
	//	                    });
	//	                    return;
	//	                }
	//
	//	                locateService.findAddressByLocator1($scope.locateform.locateControl.url, street1 + " and " + street2, "", "", zip, false, function (result) {
	//	                    esriLoader.loadModules(["esri/geometry/Point"], srsApp.CONFIG.version.arcgisVersion).then(
	//						 function ([Point]) {
	//	                            if (result[0].score == 0) {
	//	                                alertsService.error({
	//	                                    title: "Search Error",
	//	                                    message: "Intersection not found.",
	//	                                    startTop: $("#map").offset().top
	//	                                });
	//	                                return;
	//	                            };
	//	                            var coord = result[0].location;
	//	                            var point = new Point(coord.x, coord.y);
	//	                            $rootScope.$broadcast("AddPointLocate&Zoom", {
	//	                                point: point,
	//	                                zoom: 18,
	//	                                search: search
	//	                            });
	//	                        })
	//	                });
	//
	//	                break;
	//	            case "apn":
	//	                if ($scope.locateform.locateControl.APN) {
	//	                    var apn = $scope.locateform.locateControl.APN;
	//	                    if (search) {
	//	                        $rootScope.$broadcast("AddLocation&Zoom", { geometry: apn.geometry, search: search });
	//	                    } else {
	//	                        $rootScope.$broadcast("Show&ZoomIdentify", { geometry: apn.geometry })
	//	                    };
	//	                }
	//	                break;
	//	            case "dynamic":
	//	                if ($scope.locateform.locateControl.dynamic) {
	//	                    var dynamic = $scope.locateform.locateControl.dynamic;
	//	                    var ext = dynamic.geometry.getExtent();
	//	                    if (ext) {
	//	                        $rootScope.$broadcast("AddLocation&Zoom", {
	//	                            geometry: dynamic.geometry,
	//	                            search: search
	//	                        });
	//	                    } else {
	//	                        $rootScope.$broadcast("AddPointLocate&Zoom", {
	//	                            point: dynamic.geometry,
	//	                            zoom: 20,
	//	                            search: search
	//	                        });
	//	                    };
	//	                }
	//	                break;
	//	            case "image_path":
	//	                if ($scope.locateform.locateControl.image) {
	//	                    var image = $scope.locateform.locateControl.image;
	//	                    $rootScope.$broadcast("AddLocation&Zoom", {
	//	                        geometry: image.geometry,
	//	                        search: search
	//	                    });
	//	                }
	//	                break;
	//	            case "image_point":
	//	                if ($scope.locateform.locateControl.image) {
	//	                    var image = $scope.locateform.locateControl.image;
	//	                    $rootScope.$broadcast("AddPointLocate&Zoom", {
	//	                        point: image.geometry,
	//	                        zoom: 18,
	//	                        search: search
	//	                    });
	//	                }
	//	                break;
	//	            case "measureToGeometry":
	//	                $scope.locateValues.noResults = false;
	//	                $scope.locateValues.results = [];
	//	                var param = $scope.locateform.locateControl.urlParamStr;
	//	                for (var f in $scope.locateform.locateControl.fields) {
	//	                    var field = $scope.locateform.locateControl.fields[f];
	//	                    var fieldValue = $scope.locateValues[$scope.locateform.locateControl.name + "_" + field.name];
	//	                    if (fieldValue.attributes) {
	//	                        param = param.replace("{" + field.name + "}", fieldValue.attributes[field.fieldToUse]);
	//	                    } else {
	//	                        param = param.replace("{" + field.name + "}", fieldValue);
	//	                    }
	//	                }
	//	                $scope.locateform.locateControl.urlParam = param;
	//	                //assume preload data already has geometry;
	//	                esriLoader.loadModules(["esri/geometry/Polyline"], srsApp.CONFIG.version.arcgisVersion).then(
	//					 function ([Polyline]) {
	//	                        locateService.getMeasureToGeometry($scope.locateform.locateControl).then(function (data) {
	//	                            if (!($scope.searchBuffer.value > 0)) {
	//	                                $scope.searchBuffer.value = 1;
	//	                            }
	//	                            if (search) {


	//		this.mapService.broadcastAddAOIEvent({
	//		forceDraw: true, forceZoom: true,
	//		geometry: new Polyline(data.locations[0].geometry.paths),
	//		buffer: { distance: $scope.searchBuffer.value, unit: $scope.lengthUnit }
	//	});

	//	                            } else {
	//	                                $rootScope.$broadcast("Show&ZoomIdentify", { geometry: new Polyline(data.locations[0].geometry.paths) })
	//	                            }
	//	                        }, function () {
	//	                            alertsService.error({
	//	                                message: "Failed to retrieve geometry for given milepost range."
	//	                            });
	//	                        });
	//	                });
	//	                break;
	//	            case "buildout":
	//	                $scope.locateValues.noResults = false;
	//	                $scope.locateValues.results = [];
	//	                var url = $scope.locateform.locateControl.url;
	//	                if (url) {
	//	                    for (var f in $scope.locateform.locateControl.fields) {
	//	                        var field = $scope.locateform.locateControl.fields[f];
	//
	//	                        var fieldValue = $scope.locateValues[$scope.locateform.locateControl.name + "_" + field.name];
	//	                        url = url.replace("{" + field.name + "}", fieldValue);
	//	                        if (fieldValue == null || fieldValue === "") {
	//	                            alertsService.error({
	//	                                title: "Locate Error",
	//	                                message: "Field(s) cannot be empty.",
	//	                            });
	//	                            return;
	//	                        }
	//	                    }
	//	                    $http({
	//	                        url: url
	//	                    }).then(function (response) {
	//	                        $scope.locateValues.results = [{ name: $scope.locateform.locateControl.resultName.split(".").reduce(function (a, b) { return a[b] }, response) }];
	//
	//	                        esriLoader.loadModules(["esri/geometry/Point"], srsApp.CONFIG.version.arcgisVersion).then(
	//							 function ([Point]) {
	//	                                var x = $scope.locateform.locateControl.xResult.split(".").reduce(function (a, b) { return a[b] }, response);
	//	                                var y = $scope.locateform.locateControl.yResult.split(".").reduce(function (a, b) { return a[b] }, response);
	//	                                var point = new Point(x, y);
	//	                                $rootScope.$broadcast("AddPointLocate&Zoom", {
	//	                                    point: point,
	//	                                    zoom: 18,
	//	                                    search: search,
	//	                                    buffer: $scope.searchBuffer.value * $scope.searchBufferMultiplier
	//	                                });
	//	                            });
	//
	//	                    }, function (error) {
	//	                        $scope.locateValues.noResults = true
	//	                    });
	//	                }
	//	                break;
	//	            default:
	//	                break;
	//	        };
	//	    } else {
	//	        //no locate type;
	//	    }
	//	};

	// NOTE: This call is issued from locate.html (custom San Diego component?)
	//	private processSections(locateControl: any): void {
	//	    this.locateService.processSection(locateControl);
	//	}

	// NOTE: This is used by the non-default locate units
	//	    $scope.$on("UpdateAOIByExtent", function (event, arg) {
	//		console.log("TODO: UpdateAOIByExtent");
	//                        var geom;
	//                        if (!arg.geometry.extent) {
	//                            var geo = webMercatorUtils.webMercatorToGeographic(arg.geometry);
	//                            var y = geo.y;
	//                            var x = geo.x;
	//                            var d = 1000 * 10.32//100 * 1;
	//                            var point = new esri.geometry.Point([x * 1, y * 1]);
	//                            geom = new Circle(point, {
	//                                "radius": d,
	//                                "radiusUnit": "meters",
	//                                geodesic: true
	//                            });
	//
	//                            for (var c = 0; c < geom.rings[0].length; c++) {
	//                                geom.rings[0][c] = webMercatorUtils.lngLatToXY(geom.rings[0][c][0], geom.rings[0][c][1]);
	//                            }
	//
	//                            geom.spatialReference.wkid = 102100;
	//                            geom._srsShape = "circle";
	//                        } else {
	//                            var extent = webMercatorUtils.webMercatorToGeographic(arg.geometry.extent);
	//                            var xmin = extent.xmin;
	//                            var xmax = extent.xmax;
	//                            var ymin = extent.ymin;
	//                            var ymax = extent.ymax;
	//                            geom = Polygon(new SpatialReference({
	//                                wkid: 4326
	//                            }));
	//                            geom.addRing([
	//                                [xmin, ymax],
	//                                [xmax, ymax],
	//                                [xmax, ymin],
	//                                [xmin, ymin],
	//                                [xmin, ymax]
	//                            ]);
	//                        }
	//
	//							this.mapService.broadcastAddAOIEvent( { geometry: geom, forceDraw: true } );
	//	     });
}
